import { useState } from "react";
import Confetti from "../../components/Confetti";
import { track } from "../../lib/track";

const MASCOT_CORRECT = [
  "Yes! You nailed it! ⭐",
  "Boom! That's exactly right! 🎉",
  "Amazing! You're a money genius! 🔥",
  "Perfect! I knew you'd get it! 💡",
  "Wow! You're crushing it! 🚀",
];
const MASCOT_WRONG = [
  "No worries — now you know! 💪",
  "Almost! Every mistake teaches something! 🌱",
  "Don't give up — you've got this! 😊",
  "That's okay! Keep practicing! 📚",
  "Next one's yours! I believe in you! 🙌",
];

export default function LessonScreen({world,lessonIdx,progress,onComplete,onBack,tier}){
  const dark = tier === "master";
  const lesson=world.lessons[lessonIdx];
  const qs=lesson.quiz;
  const isScenario=lesson.type==="scenario";
  const [phase,setPhase]=useState(isScenario?"setup":"story");
  const [qIdx,setQIdx]=useState(0);
  const [answers,setAnswers]=useState([]);
  const [submitted,setSubmitted]=useState(false);
  const [showConf,setShowConf]=useState(false);
  const [scenarioPick,setScenarioPick]=useState<number|null>(null);
  const [showAllOutcomes,setShowAllOutcomes]=useState(false);
  const [showExitWarning,setShowExitWarning]=useState(false);

  const curQ=qs[qIdx];
  const curAns=answers[qIdx]??null;
  const isLastQ=qIdx===qs.length-1;
  const allCorrect=qs.every((q,i)=>answers[i]===q.a);
  const score=qs.filter((q,i)=>answers[i]===q.a).length;

  function selectAnswer(i){
    if(submitted)return;
    setAnswers(prev=>{const n=[...prev];n[qIdx]=i;return n;});
  }

  function submitCurrent(){
    if(curAns===null)return;
    setSubmitted(true);
    track("quiz_answered", { correct: curAns === curQ.a });
    if(curAns===curQ.a&&isLastQ){
      setShowConf(true);setTimeout(()=>setShowConf(false),2000);
    }
  }

  function nextQ(){
    setSubmitted(false);
    setQIdx(q=>q+1);
  }

  function finish(){
    onComplete(world.id,lessonIdx,allCorrect);
    onBack();
  }

  const hasConceptPhase=!!(lesson.concepts||lesson.didYouKnow);
  const phases=isScenario?["setup","consequence","quiz"]:hasConceptPhase?["story","concepts","quiz"]:["story","quiz"];
  const phaseLabels=isScenario?["🎭 Scenario","💡 Reveal","🧠 Quiz"]:hasConceptPhase?["📖 Story","🔑 Key Ideas","🧠 Quiz"]:["📖 Story","🧠 Quiz"];
  const phaseIdx=phases.indexOf(phase);
  const outcomeStyle=(outcome:string)=>{
    if(outcome==="great")return{color:world.color,bg:`${world.color}18`,border:`2.5px solid ${world.color}`};
    if(outcome==="good")return{color:"#d97706",bg:dark?"#451a03":"#fffbeb",border:"2.5px solid #f59e0b"};
    return{color:dark?"#fca5a5":"#991b1b",bg:dark?"#450a0a":"#fee2e2",border:"2.5px solid #ef4444"};
  };

  return(
    <div style={{fontFamily:"Nunito",minHeight:"100vh",background:dark?"#0f172a":world.bg,paddingBottom:30}}>
      {showConf&&<Confetti/>}

      {/* Exit quiz confirmation overlay */}
      {showExitWarning&&(
        <div style={{position:"fixed",inset:0,background:"#00000077",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
          <div style={{background:dark?"#1e293b":"#fff",borderRadius:24,padding:"28px 24px",width:"100%",maxWidth:340,border:dark?"2px solid #334155":`3px solid ${world.color}55`,boxShadow:"0 16px 48px rgba(0,0,0,0.35)",textAlign:"center"}}>
            <div style={{fontSize:38,marginBottom:10}}>⚠️</div>
            <div style={{fontSize:18,fontWeight:900,color:dark?"#f1f5f9":"#1f2937",fontFamily:"Fredoka One",marginBottom:8}}>Leave quiz?</div>
            <div style={{fontSize:13,color:dark?"#94a3b8":"#6b7280",fontWeight:700,marginBottom:20,lineHeight:1.5}}>Your answers won't be saved. You'll need to start the quiz again.</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowExitWarning(false)}
                style={{flex:1,background:dark?"#0f172a":"#f1f5f9",border:dark?"2px solid #334155":"2px solid #e5e7eb",borderRadius:14,padding:"12px",fontSize:13,fontWeight:900,color:dark?"#94a3b8":"#6b7280",cursor:"pointer",fontFamily:"Nunito"}}>
                Keep going
              </button>
              <button onClick={onBack}
                style={{flex:1,background:"linear-gradient(135deg,#ef4444,#dc2626)",border:"none",borderRadius:14,padding:"12px",fontSize:13,fontWeight:900,color:"#fff",cursor:"pointer",fontFamily:"Nunito"}}>
                Leave quiz
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={{
        background:dark?"#1e293b":"#fff",
        borderBottom:dark?"1px solid #334155":`3px solid ${world.color}55`,
        padding:"12px 16px",display:"flex",alignItems:"center",gap:12,
        position:"sticky",top:0,zIndex:10,
        boxShadow:dark?"0 2px 12px #00000055":"0 2px 12px #0001"}}>
        <button
          onClick={() => phase==="quiz"&&!(isLastQ&&submitted) ? setShowExitWarning(true) : onBack()}
          className="btn-bounce"
          style={{
            background:dark?`${world.color}22`:`${world.color}22`,
            border:dark?`2px solid ${world.color}44`:`2px solid ${world.color}55`,
            borderRadius:12,padding:"6px 12px",fontSize:13,fontWeight:800,
            color:dark?"#e2e8f0":world.dark,cursor:"pointer"}}>← Back</button>
        <div style={{flex:1}}>
          <div style={{fontSize:10,color:dark?"#6366f1":world.color,fontWeight:900,textTransform:"uppercase",letterSpacing:1.5}}>{world.label}</div>
          <div style={{fontSize:15,fontWeight:900,color:dark?"#f1f5f9":world.dark,fontFamily:"Fredoka One",lineHeight:1.1}}>{lesson.title}</div>
        </div>
        {!dark&&<div style={{fontSize:30}}>🐿️</div>}
      </div>

      {/* Phase progress dots */}
      <div style={{display:"flex",gap:6,padding:"12px 16px 0",justifyContent:"center"}}>
        {phases.map((p,i)=>(
          <div key={p} style={{height:8,flex:1,maxWidth:64,borderRadius:8,
            background:phaseIdx>=i?world.color:dark?"#334155":"#d1d5db",
            transition:"background 0.4s",boxShadow:phaseIdx===i?`0 2px 8px ${world.color}66`:"none"}}/>
        ))}
      </div>
      <div style={{display:"flex",gap:6,padding:"3px 16px 10px",justifyContent:"center"}}>
        {phaseLabels.map((lbl,i)=>(
          <div key={i} style={{flex:1,maxWidth:64,textAlign:"center",fontSize:9,fontWeight:800,
            color:phaseIdx>=i?world.color:dark?"#475569":"#9ca3af",transition:"color 0.3s"}}>{lbl}</div>
        ))}
      </div>

      <div style={{padding:"0 16px"}}>

        {/* SCENARIO — SETUP */}
        {phase==="setup"&&isScenario&&(
          <div style={{animation:"slideUp 0.4s"}}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:36,marginBottom:4}}>🎭</div>
              <div style={{fontSize:12,fontWeight:900,color:world.color,textTransform:"uppercase",letterSpacing:1.5}}>
                What Would You Do?
              </div>
            </div>
            <div style={{
              background:dark?"#1e293b":"#fff",borderRadius:22,padding:"20px 18px",
              border:dark?`2px solid #334155`:`3px solid ${world.color}30`,
              boxShadow:dark?`0 8px 28px #00000055`:`0 8px 28px ${world.color}22`,
              marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                <div style={{fontSize:32}}>{lesson.characterEmoji}</div>
                <div style={{fontSize:14,fontWeight:800,color:dark?"#94a3b8":"#6b7280"}}>{lesson.character}</div>
              </div>
              <div style={{fontSize:15,fontWeight:700,color:dark?"#e2e8f0":"#1f2937",lineHeight:1.65}}>
                {lesson.setup}
              </div>
            </div>
            <div style={{fontSize:11,fontWeight:900,color:dark?"#94a3b8":world.color,
              textTransform:"uppercase",letterSpacing:1.5,marginBottom:10,paddingLeft:2}}>
              Choose your path:
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {lesson.choices.map((c:any,i:number)=>{
                const picked=scenarioPick===i;
                return(
                  <button key={i} onClick={()=>setScenarioPick(i)} className="btn-bounce"
                    style={{display:"flex",alignItems:"center",gap:12,
                      background:picked?`${world.color}22`:dark?"#1e293b":"#f9fafb",
                      border:picked?`2.5px solid ${world.color}`:dark?"2px solid #334155":"2px solid #e5e7eb",
                      borderRadius:16,padding:"15px 14px",cursor:"pointer",textAlign:"left",
                      transition:"all 0.2s",boxShadow:picked?`0 4px 14px ${world.color}33`:"none"}}>
                    <div style={{fontSize:28,flexShrink:0}}>{c.emoji}</div>
                    <div style={{fontSize:15,fontWeight:700,color:dark?"#e2e8f0":"#374151",lineHeight:1.4}}>{c.label}</div>
                  </button>
                );
              })}
            </div>
            <button onClick={()=>setPhase("consequence")} disabled={scenarioPick===null} className="btn-bounce"
              style={{width:"100%",
                background:scenarioPick!==null?`linear-gradient(135deg,${world.color},${world.dark})`:(dark?"#1e293b":"#d1d5db"),
                border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                color:scenarioPick!==null?"#fff":(dark?"#475569":"#9ca3af"),
                cursor:scenarioPick!==null?"pointer":"not-allowed",fontFamily:"Fredoka One",
                boxShadow:scenarioPick!==null?`0 8px 24px ${world.color}55`:"none",transition:"all 0.3s"}}>
              See What Happens →
            </button>
          </div>
        )}

        {/* SCENARIO — CONSEQUENCE */}
        {phase==="consequence"&&isScenario&&scenarioPick!==null&&(()=>{
          const picked=lesson.choices[scenarioPick];
          const os=outcomeStyle(picked.outcome);
          return(
            <div style={{animation:"slideUp 0.4s"}}>
              <div style={{textAlign:"center",marginBottom:14}}>
                <div style={{display:"inline-block",background:os.bg,border:os.border,
                  borderRadius:20,padding:"6px 18px",fontSize:14,fontWeight:900,color:os.color}}>
                  {picked.outcomeLabel}
                </div>
              </div>
              <div style={{
                background:dark?"#1e293b":"#fff",borderRadius:22,padding:"20px 18px",
                border:os.border,
                boxShadow:dark?`0 8px 28px #00000055`:`0 8px 28px ${world.color}22`,
                marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                  <div style={{fontSize:28}}>{picked.emoji}</div>
                  <div style={{fontSize:14,fontWeight:800,color:dark?"#94a3b8":"#6b7280"}}>
                    You chose: <span style={{color:os.color}}>{picked.label}</span>
                  </div>
                </div>
                <div style={{fontSize:15,fontWeight:700,color:dark?"#e2e8f0":"#374151",lineHeight:1.65}}>
                  {picked.consequence}
                </div>
              </div>
              <button onClick={()=>setShowAllOutcomes(s=>!s)}
                style={{background:"none",border:`2px solid ${world.color}44`,borderRadius:12,
                  padding:"8px 14px",fontSize:13,fontWeight:800,
                  color:dark?"#94a3b8":world.color,cursor:"pointer",marginBottom:12,width:"100%"}}>
                {showAllOutcomes?"Hide other outcomes ▲":"See all outcomes ▼"}
              </button>
              {showAllOutcomes&&(
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                  {lesson.choices.map((c:any,i:number)=>{
                    if(i===scenarioPick)return null;
                    const cs=outcomeStyle(c.outcome);
                    return(
                      <div key={i} style={{
                        background:dark?"#1e293b":"#f9fafb",borderRadius:16,padding:"14px",
                        border:dark?"2px solid #334155":"2px solid #e5e7eb"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                          <div style={{fontSize:22}}>{c.emoji}</div>
                          <div style={{fontSize:13,fontWeight:800,color:dark?"#94a3b8":"#6b7280",flex:1}}>{c.label}</div>
                          <div style={{flexShrink:0,fontSize:11,fontWeight:900,
                            background:cs.bg,border:cs.border,
                            borderRadius:12,padding:"3px 10px",color:cs.color}}>
                            {c.outcomeLabel}
                          </div>
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:dark?"#94a3b8":"#6b7280",lineHeight:1.5}}>
                          {c.consequence}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{
                background:dark?`#0f172a`:`linear-gradient(135deg,${world.color}20,${world.color}0a)`,
                borderRadius:16,padding:"14px 16px",marginBottom:16,
                border:dark?`2px solid ${world.color}44`:`2px solid ${world.color}44`}}>
                <div style={{fontSize:11,fontWeight:900,color:world.color,textTransform:"uppercase",
                  letterSpacing:1.5,marginBottom:6}}>💡 The Lesson</div>
                <div style={{fontSize:15,fontWeight:800,color:dark?"#e2e8f0":world.dark,lineHeight:1.55}}>
                  {lesson.lesson}
                </div>
              </div>
              <button onClick={()=>setPhase("quiz")} className="btn-bounce"
                style={{width:"100%",background:`linear-gradient(135deg,${world.color},${world.dark})`,
                  border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                  color:"#fff",cursor:"pointer",fontFamily:"Fredoka One",letterSpacing:0.5,
                  boxShadow:`0 8px 24px ${world.color}55`}}>
                Take the Quiz! 🧠
              </button>
            </div>
          );
        })()}

        {/* STORY */}
        {phase==="story"&&(
          <div style={{animation:"slideUp 0.4s"}}>
            <div style={{
              background:dark?"#1e293b":"#fff",borderRadius:22,padding:"20px 18px",
              border:dark?`2px solid #334155`:`3px solid ${world.color}30`,
              boxShadow:dark?`0 8px 28px #00000055`:`0 8px 28px ${world.color}22`,
              marginBottom:14}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:14}}>
                {!dark&&<div style={{fontSize:42,animation:"float 3s infinite",lineHeight:1,flexShrink:0}}>🐿️</div>}
                <div>
                  {!dark&&<div style={{fontSize:12,fontWeight:900,color:world.color,marginBottom:5,textTransform:"uppercase",letterSpacing:1}}>Stash says:</div>}
                  <div style={{fontSize:15,fontWeight:700,color:dark?"#cbd5e1":"#374151",lineHeight:1.65}}>{lesson.story}</div>
                </div>
              </div>
              <div style={{
                background:dark?`#0f172a`:`linear-gradient(135deg,${world.color}20,${world.color}0a)`,
                borderRadius:14,padding:"11px 14px",
                border:dark?`2px solid ${world.color}44`:`2px solid ${world.color}44`}}>
                <div style={{fontSize:14,fontWeight:800,color:dark?"#e2e8f0":world.dark,lineHeight:1.4}}>{lesson.tip}</div>
              </div>
            </div>
            <button onClick={()=>setPhase(hasConceptPhase?"concepts":"quiz")} className="btn-bounce"
              style={{width:"100%",background:`linear-gradient(135deg,${world.color},${world.dark})`,
                border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                color:"#fff",cursor:"pointer",fontFamily:"Fredoka One",letterSpacing:0.5,
                boxShadow:`0 8px 24px ${world.color}55`}}>
              {hasConceptPhase?"Key Ideas 🔑":"Start Quiz! 🧠"}
            </button>
          </div>
        )}

        {/* KEY IDEAS (concepts + did you know) */}
        {phase==="concepts"&&(
          <div style={{animation:"slideUp 0.4s"}}>

            {/* Did You Know card */}
            {lesson.didYouKnow&&(
              <div style={{
                background:dark?`${world.color}18`:`linear-gradient(135deg,${world.color}18,${world.color}08)`,
                border:`2.5px solid ${world.color}55`,
                borderRadius:22,padding:"18px 16px",marginBottom:14,
                boxShadow:dark?`0 8px 28px #00000055`:`0 8px 28px ${world.color}22`}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <div style={{fontSize:26}}>💡</div>
                  <div style={{fontSize:11,fontWeight:900,color:dark?"#e2e8f0":world.dark,
                    textTransform:"uppercase",letterSpacing:1.5}}>Did You Know?</div>
                </div>
                <div style={{fontSize:15,fontWeight:700,color:dark?"#cbd5e1":"#374151",lineHeight:1.65}}>
                  {lesson.didYouKnow}
                </div>
              </div>
            )}

            {/* Concept cards */}
            {lesson.concepts&&lesson.concepts.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:900,
                  color:dark?"#94a3b8":world.color,
                  textTransform:"uppercase",letterSpacing:1.5,marginBottom:10,paddingLeft:2}}>
                  🔑 Key Concepts
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {lesson.concepts.map((c:any,i:number)=>(
                    <div key={i} style={{
                      background:dark?"#1e293b":"#fff",
                      border:dark?`2px solid #334155`:`2.5px solid ${world.color}30`,
                      borderRadius:18,padding:"16px",
                      boxShadow:dark?`0 4px 18px #00000044`:`0 4px 18px ${world.color}18`,
                      display:"flex",alignItems:"flex-start",gap:14}}>
                      <div style={{fontSize:36,lineHeight:1,flexShrink:0}}>{c.emoji}</div>
                      <div>
                        <div style={{fontSize:17,fontWeight:900,color:dark?"#f1f5f9":world.dark,
                          fontFamily:"Fredoka One",marginBottom:5}}>{c.term}</div>
                        <div style={{fontSize:14,fontWeight:700,color:dark?"#94a3b8":"#6b7280",
                          lineHeight:1.55}}>{c.def}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={()=>setPhase("quiz")} className="btn-bounce"
              style={{width:"100%",background:`linear-gradient(135deg,${world.color},${world.dark})`,
                border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                color:"#fff",cursor:"pointer",fontFamily:"Fredoka One",letterSpacing:0.5,
                boxShadow:`0 8px 24px ${world.color}55`}}>
              Start Quiz! 🧠
            </button>
          </div>
        )}

        {/* QUIZ */}
        {phase==="quiz"&&(
          <div style={{animation:"slideUp 0.4s"}}>

            {/* Question counter + progress bar */}
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:12,fontWeight:900,color:world.color,textTransform:"uppercase",letterSpacing:1.5}}>
                  🧠 Question {qIdx+1} of {qs.length}
                </div>
                <div style={{fontSize:12,fontWeight:800,color:dark?"#94a3b8":"#6b7280"}}>
                  {answers.filter((a,i)=>a===qs[i].a).length}/{qs.length} correct
                </div>
              </div>
              <div style={{display:"flex",gap:5}}>
                {qs.map((_,i)=>{
                  const answered=answers[i]!==undefined;
                  const correct=answers[i]===qs[i].a;
                  const isCur=i===qIdx;
                  return(
                    <div key={i} style={{flex:1,height:8,borderRadius:6,
                      background:answered?(correct?world.color:"#f87171"):isCur?world.color+"55":dark?"#334155":"#e5e7eb",
                      border:isCur?`2px solid ${world.color}`:"2px solid transparent",
                      transition:"all 0.3s"}}/>
                  );
                })}
              </div>
            </div>

            {/* Question card */}
            <div style={{
              background:dark?"#1e293b":"#fff",borderRadius:22,padding:"20px 18px",
              border:dark?`2px solid #334155`:`3px solid ${world.color}30`,
              boxShadow:dark?`0 8px 28px #00000055`:`0 8px 28px ${world.color}22`,
              marginBottom:14}}>
              <div style={{fontSize:16,fontWeight:800,color:dark?"#f1f5f9":"#1f2937",marginBottom:16,lineHeight:1.5}}>
                {curQ.q}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:9}}>
                {curQ.opts.map((o,i)=>{
                  const letters=["🅰️","🅱️","🅲️","🅳️"];
                  let bg=dark?"#0f172a":"#f9fafb";
                  let border=dark?`2px solid #334155`:`2px solid #e5e7eb`;
                  let clr=dark?"#cbd5e1":"#374151";
                  if(submitted){
                    if(i===curQ.a){bg=`${world.color}22`;border=`2.5px solid ${world.color}`;clr=dark?"#e2e8f0":world.dark;}
                    else if(i===curAns&&i!==curQ.a){bg=dark?"#450a0a":"#fee2e2";border="2.5px solid #ef4444";clr=dark?"#fca5a5":"#991b1b";}
                  } else if(curAns===i){bg=`${world.color}20`;border=`2.5px solid ${world.color}`;clr=dark?"#e2e8f0":world.dark;}
                  return(
                    <button key={i} onClick={()=>selectAnswer(i)}
                      className="btn-bounce"
                      style={{display:"flex",alignItems:"center",gap:10,background:bg,
                        border,borderRadius:14,padding:"13px 14px",
                        cursor:submitted?"default":"pointer",
                        fontSize:14,fontWeight:700,color:clr,textAlign:"left",
                        transition:"all 0.2s",
                        boxShadow:curAns===i&&!submitted?`0 4px 14px ${world.color}33`:"none"}}>
                      <span style={{fontSize:20,flexShrink:0}}>{letters[i]}</span>{o}
                    </button>
                  );
                })}
              </div>
              {submitted&&(
                <div style={{marginTop:14,animation:"pop 0.4s"}}>
                  <div style={{
                    background:curAns===curQ.a?`${world.color}15`:dark?"#451a03":"#fffbeb",
                    border:`2.5px solid ${curAns===curQ.a?world.color:"#f59e0b"}`,
                    borderRadius:16,padding:"12px 14px",marginBottom:!dark?10:0}}>
                    <div style={{fontSize:14,fontWeight:900,
                      color:curAns===curQ.a?(dark?"#e2e8f0":world.dark):dark?"#fbbf24":"#92400e",lineHeight:1.4}}>
                      {curAns===curQ.a
                        ?`🎉 ${curQ.praise}`
                        :`💡 The answer was: "${curQ.opts[curQ.a]}" — keep going!`}
                    </div>
                  </div>
                  {!dark&&(
                    <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                      <div style={{fontSize:30,flexShrink:0,animation:curAns===curQ.a?"bounce 0.6s 2":"float 3s infinite"}}>
                        🐿️
                      </div>
                      <div style={{
                        background:"#fff",borderRadius:16,borderTopLeftRadius:4,
                        padding:"8px 12px",flex:1,
                        border:`2px solid ${world.color}44`,
                        boxShadow:`0 3px 10px ${world.color}18`}}>
                        <div style={{fontSize:10,fontWeight:900,color:world.color,
                          textTransform:"uppercase",letterSpacing:1,marginBottom:3}}>
                          Stash says:
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:world.dark,lineHeight:1.4}}>
                          {curAns===curQ.a
                            ?MASCOT_CORRECT[qIdx%MASCOT_CORRECT.length]
                            :MASCOT_WRONG[qIdx%MASCOT_WRONG.length]}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action button */}
            {!submitted?(
              <button onClick={submitCurrent} disabled={curAns===null}
                className="btn-bounce"
                style={{width:"100%",
                  background:curAns===null?(dark?"#1e293b":"#d1d5db"):`linear-gradient(135deg,${world.color},${world.dark})`,
                  border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                  color:curAns===null?(dark?"#475569":"#fff"):"#fff",
                  cursor:curAns===null?"not-allowed":"pointer",fontFamily:"Fredoka One",
                  boxShadow:curAns!==null?`0 8px 24px ${world.color}55`:"none",transition:"all 0.3s"}}>
                Check Answer! ✅
              </button>
            ):isLastQ?(
              <div>
                <div style={{
                  background:dark?"#1e293b":"#fff",borderRadius:18,padding:"16px",
                  border:dark?`2px solid #334155`:`3px solid ${world.color}44`,
                  marginBottom:12,textAlign:"center",
                  boxShadow:dark?`0 6px 20px #00000055`:`0 6px 20px ${world.color}22`,
                  animation:"pop 0.4s"}}>
                  <div style={{fontSize:36,marginBottom:6}}>
                    {score===qs.length?"🏆":score>=qs.length/2?"⭐":"📚"}
                  </div>
                  <div style={{fontSize:20,fontWeight:900,color:dark?"#f1f5f9":world.dark,fontFamily:"Fredoka One"}}>
                    {score===qs.length?"Perfect Score!":score>=qs.length/2?"Nice Work!":"Keep Practicing!"}
                  </div>
                  <div style={{fontSize:15,color:dark?"#94a3b8":"#6b7280",fontWeight:700,marginTop:4}}>
                    You got {score} out of {qs.length} correct!
                  </div>
                  {!dark&&(
                    <div style={{display:"flex",alignItems:"center",gap:10,justifyContent:"center",
                      marginTop:12,paddingTop:12,borderTop:`1.5px solid ${world.color}22`}}>
                      <div style={{fontSize:42,animation:score===qs.length?"bounce 0.8s 3":"float 3s infinite",flexShrink:0}}>
                        🐿️
                      </div>
                      <div style={{fontSize:14,fontWeight:800,color:world.dark,textAlign:"left",lineHeight:1.4,maxWidth:170}}>
                        {score===qs.length
                          ?"Stash is SO proud of you! 🏆"
                          :score>=qs.length/2
                          ?"Stash thinks you did great! Keep going! ⭐"
                          :"Stash says: practice makes perfect! 💪"}
                      </div>
                    </div>
                  )}
                  <div style={{display:"flex",gap:6,justifyContent:"center",marginTop:10}}>
                    {qs.map((q,i)=>(
                      <div key={i} style={{width:32,height:32,borderRadius:"50%",
                        background:answers[i]===q.a?world.color:"#fee2e2",
                        border:`2px solid ${answers[i]===q.a?world.dark:"#ef4444"}`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:14,fontWeight:900,color:"#fff"}}>
                        {answers[i]===q.a?"✓":"✗"}
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={finish} className="btn-bounce"
                  style={{width:"100%",background:`linear-gradient(135deg,${world.color},${world.dark})`,
                    border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                    color:"#fff",cursor:"pointer",fontFamily:"Fredoka One",
                    boxShadow:`0 8px 24px ${world.color}55`,animation:"pulse 1.2s infinite"}}>
                  {score===qs.length?"Collect Reward! 🪙✨":"Finish Lesson →"}
                </button>
              </div>
            ):(
              <button onClick={nextQ} className="btn-bounce"
                style={{width:"100%",background:`linear-gradient(135deg,${world.color},${world.dark})`,
                  border:"none",borderRadius:18,padding:"17px",fontSize:18,fontWeight:900,
                  color:"#fff",cursor:"pointer",fontFamily:"Fredoka One",
                  boxShadow:`0 8px 24px ${world.color}55`}}>
                Next Question →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
